import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Table,
  Spin,
  Alert,
  Typography,
  Input,
  Button,
  Space,
  Modal,
  DatePicker,
  Tag,
} from 'antd';
import { getAllHubtelCallbackRecords } from '../Redux/slice/paymentSlice';
import { DownloadOutlined, ReloadOutlined } from '@ant-design/icons';
import * as XLSX from 'xlsx';
import dayjs from 'dayjs';

const { Title } = Typography;
const { Search } = Input;
const { RangePicker } = DatePicker;

const statusColors = {
  Success: 'green',
  Failed: 'red',
  Pending: 'orange',
};

const paymentTypeColors = {
  mobilemoney: 'orange',
  CreditCard: 'green',
  mastercard: 'gold',
  string: 'default',
};

const CallbackRecordsPage = () => {
  const dispatch = useDispatch();
  const { callbackData, loading, error } = useSelector((state) => state.payment);

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDetails, setSelectedDetails] = useState(null);
  const [dateRange, setDateRange] = useState(null);

  useEffect(() => {
    dispatch(getAllHubtelCallbackRecords());
  }, [dispatch]);

  const generatedData = Array.isArray(callbackData)
    ? callbackData.map((item, index) => {
        const fakeDate = dayjs().subtract(index, 'day');
        return {
          key: index,
          date: fakeDate,
          dateStr: fakeDate.format('YYYY-MM-DD'),
          responseCode: item.responseCode,
          checkoutId: item.checkoutId,
          salesInvoiceId: item.salesInvoiceId,
          clientReference: item.clientReference,
          transactionStatus: item.status,
          amount: item.amount,
          customerPhoneNumber: item.customerPhoneNumber,
          paymentType: item.paymentType,
          mobileMoneyNumber: item.mobileMoneyNumber,
          channel: item.channel,
          rawPaymentDetails: {
            description: item.description,
            ...item,
          },
        };
      })
    : [];

  const filteredData = generatedData.filter((record) => {
    const query = searchQuery.toLowerCase();
    const matchesQuery =
      !query ||
      record.clientReference?.toLowerCase().includes(query) ||
      record.customerPhoneNumber?.includes(query) ||
      record.transactionStatus?.toLowerCase().includes(query) ||
      record.paymentType?.toLowerCase().includes(query);

    const matchesDate =
      !dateRange ||
      record.date.isBetween(dateRange[0], dateRange[1], 'day', '[]');

    return matchesQuery && matchesDate;
  });

  const handleExportToExcel = () => {
    const rowsToExport = filteredData.map(({ rawPaymentDetails, date, ...rest }) => ({
      ...rest,
      date: date.format('YYYY-MM-DD'),
      rawPaymentDetails: JSON.stringify(rawPaymentDetails),
    }));

    const worksheet = XLSX.utils.json_to_sheet(rowsToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Hubtel Records');
    XLSX.writeFile(workbook, 'hubtel_callbacks.xlsx');
  };

  const columns = [
    {
      title: 'Date',
      dataIndex: 'dateStr',
      key: 'dateStr',
      sorter: (a, b) => a.date.unix() - b.date.unix(),
    },
    {
      title: 'Client Ref',
      dataIndex: 'clientReference',
      key: 'clientReference',
      sorter: (a, b) => a.clientReference.localeCompare(b.clientReference),
    },
    {
      title: 'Checkout ID',
      dataIndex: 'checkoutId',
      key: 'checkoutId',
    },
    {
      title: 'Invoice ID',
      dataIndex: 'salesInvoiceId',
      key: 'salesInvoiceId',
    },
    {
      title: 'Status',
      dataIndex: 'transactionStatus',
      key: 'transactionStatus',
      filters: [
        { text: 'Success', value: 'Success' },
        { text: 'Pending', value: 'Pending' },
        { text: 'Failed', value: 'Failed' },
      ],
      onFilter: (value, record) => record.transactionStatus === value,
      render: (status) => (
        <Tag color={statusColors[status] || 'default'}>{status}</Tag>
      ),
    },
    {
      title: 'Amount (GHS)',
      dataIndex: 'amount',
      key: 'amount',
      sorter: (a, b) => a.amount - b.amount,
    },
    {
      title: 'Phone Number',
      dataIndex: 'customerPhoneNumber',
      key: 'customerPhoneNumber',
    },
    {
      title: 'Payment Type',
      dataIndex: 'paymentType',
      key: 'paymentType',
      render: (type) => (
        <Tag color={paymentTypeColors[type?.toLowerCase()] || 'default'}>
          {type}
        </Tag>
      ),
    },
    {
      title: 'Channel',
      dataIndex: 'channel',
      key: 'channel',
    },
    {
      title: 'Raw Details',
      key: 'rawPaymentDetails',
      render: (_, record) => (
        <Button type="link" onClick={() => setSelectedDetails(record.rawPaymentDetails)}>
          View
        </Button>
      ),
    },
  ];

  return (
    <div className="p-6 bg-white rounded shadow-md">
      <div className="flex flex-wrap justify-between items-center mb-4 gap-3">
        <Title level={3} className="m-0">Hubtel Callback Records</Title>
        <Space wrap>
          <Search
            placeholder="Search phone, status, type, ref..."
            onSearch={setSearchQuery}
            allowClear
            style={{ width: 250 }}
          />
          <RangePicker onChange={setDateRange} />
          <Button icon={<ReloadOutlined />} onClick={() => dispatch(getAllHubtelCallbackRecords())}>
            Refresh
          </Button>
          <Button type="primary" icon={<DownloadOutlined />} onClick={handleExportToExcel}>
            Export
          </Button>
        </Space>
      </div>

      {loading ? (
        <Spin size="large" className="mt-6" />
      ) : error ? (
        <Alert message="Error" description={error} type="error" showIcon />
      ) : (
        <Table
          dataSource={filteredData}
          columns={columns}
          pagination={{ pageSize: 10 }}
          scroll={{ x: 1200 }}
        />
      )}

      <Modal
        open={!!selectedDetails}
        title="Raw Payment Details"
        onCancel={() => setSelectedDetails(null)}
        footer={null}
        width={700}
      >
        <pre className="text-xs bg-gray-100 p-4 rounded overflow-x-auto">
          {JSON.stringify(selectedDetails, null, 2)}
        </pre>
      </Modal>
    </div>
  );
};

export default CallbackRecordsPage;
